import React, { useEffect, useMemo, useState } from "react"
import { Shield, Baby, Pencil } from "lucide-react"
import { formatDateShort, formatMoney } from "../../utils/format"
import { getGroupTheme } from "../../utils/groupTheme"

const toDateInputValue = (value) => {
  if (!value) return ""
  const dt = new Date(value)
  if (Number.isNaN(dt.getTime())) return ""
  return dt.toISOString().slice(0, 10)
}

const toNumberInputString = (value) => {
  if (value === null || value === undefined) return ""
  const num = Number(value)
  if (!Number.isFinite(num)) return ""
  return String(num)
}

const normalizeProbationFromGroup = (probation) => {
  const unit = String(probation?.unit || "").toLowerCase()
  const value = Number(probation?.value || 0)
  if ((unit === "days" || unit === "months") && Number.isInteger(value) && value > 0) {
    return { value: String(value), unit }
  }
  return { value: "", unit: "" }
}

function GroupWelfareSettingsPanel({
  group,
  isAdmin = false,
  onSaveSettings,
  onAddChild,
  isAddingChild = false,
  isSaving = false,
  readOnly = false,
}) {
  const theme = getGroupTheme(group)
  const welfare = group?.welfare || {}
  const enrollment = welfare?.enrollment || {}
  const benefitAmounts = welfare?.benefitAmounts || {}
  const reimbursementAmounts = welfare?.reimbursementAmounts || {}
  const coverage = welfare?.coverage || {}
  const registrationFeeFromGroup = welfare?.registrationFee
  const lateFeeFromGroup = welfare?.lateFee
  const probationValueFromGroup = welfare?.probation?.value
  const probationUnitFromGroup = welfare?.probation?.unit

  const [form, setForm] = useState({
    enrollmentEnabled: false,
    enrollmentStartDate: "",
    enrollmentEndDate: "",
    registrationFee: "",
    lateFee: "",
    benefitMember: "",
    benefitSpouse: "",
    benefitSpouseParent: "",
    benefitChild: "",
    benefitSibling: "",
    benefitParent: "",
    reimbursementMember: "",
    reimbursementSpouse: "",
    reimbursementSpouseParent: "",
    reimbursementChild: "",
    reimbursementSibling: "",
    reimbursementParent: "",
    coverParent: true,
    coverSpouse: true,
    coverSpouseParent: true,
    coverChild: true,
    coverSibling: true,
    probationValue: "",
    probationUnit: "",
  })
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [childForm, setChildForm] = useState({
    fullName: "",
    dateOfBirth: "",
  })
  const [childError, setChildError] = useState("")
  const [childSuccess, setChildSuccess] = useState("")

  useEffect(() => {
    const probation = normalizeProbationFromGroup({
      value: probationValueFromGroup,
      unit: probationUnitFromGroup,
    })
    setForm({
      enrollmentEnabled: !!enrollment?.enabled,
      enrollmentStartDate: toDateInputValue(enrollment?.startDate),
      enrollmentEndDate: toDateInputValue(enrollment?.endDate),
      registrationFee: toNumberInputString(registrationFeeFromGroup),
      lateFee: toNumberInputString(lateFeeFromGroup),
      benefitMember: toNumberInputString(benefitAmounts?.member),
      benefitSpouse: toNumberInputString(benefitAmounts?.spouse),
      benefitSpouseParent: toNumberInputString(
        benefitAmounts?.spouse_parent ?? benefitAmounts?.spouseParent
      ),
      benefitChild: toNumberInputString(benefitAmounts?.child),
      benefitSibling: toNumberInputString(benefitAmounts?.sibling),
      benefitParent: toNumberInputString(benefitAmounts?.parent),
      reimbursementMember: toNumberInputString(reimbursementAmounts?.member),
      reimbursementSpouse: toNumberInputString(reimbursementAmounts?.spouse),
      reimbursementSpouseParent: toNumberInputString(
        reimbursementAmounts?.spouse_parent ?? reimbursementAmounts?.spouseParent
      ),
      reimbursementChild: toNumberInputString(reimbursementAmounts?.child),
      reimbursementSibling: toNumberInputString(reimbursementAmounts?.sibling),
      reimbursementParent: toNumberInputString(reimbursementAmounts?.parent),
      coverParent: coverage?.parent !== false,
      coverSpouse: coverage?.spouse !== false,
      coverSpouseParent: coverage?.spouseParent !== false,
      coverChild: coverage?.child !== false,
      coverSibling: coverage?.sibling !== false,
      probationValue: probation.value,
      probationUnit: probation.unit,
    })
    setError("")
    setSuccess("")
    setChildForm({ fullName: "", dateOfBirth: "" })
    setChildError("")
    setChildSuccess("")
  }, [
    group?.id,
    enrollment?.enabled,
    enrollment?.startDate,
    enrollment?.endDate,
    registrationFeeFromGroup,
    lateFeeFromGroup,
    benefitAmounts?.member,
    benefitAmounts?.spouse,
    benefitAmounts?.spouse_parent,
    benefitAmounts?.spouseParent,
    benefitAmounts?.child,
    benefitAmounts?.sibling,
    benefitAmounts?.parent,
    reimbursementAmounts?.member,
    reimbursementAmounts?.spouse,
    reimbursementAmounts?.spouse_parent,
    reimbursementAmounts?.spouseParent,
    reimbursementAmounts?.child,
    reimbursementAmounts?.sibling,
    reimbursementAmounts?.parent,
    coverage?.parent,
    coverage?.spouse,
    coverage?.spouseParent,
    coverage?.child,
    coverage?.sibling,
    probationValueFromGroup,
    probationUnitFromGroup,
  ])

  const enrollmentWindowText = useMemo(() => {
    const start = enrollment?.startDate ? formatDateShort(enrollment.startDate) : "Not set"
    const end = enrollment?.endDate ? formatDateShort(enrollment.endDate) : "Open-ended"
    return `${start} to ${end}`
  }, [enrollment?.startDate, enrollment?.endDate])
  const welfareProbationPreview = useMemo(() => {
    const probationUnit = String(form.probationUnit || "").trim().toLowerCase()
    const probationValue = Number(form.probationValue)
    const hasProbation =
      ["days", "months"].includes(probationUnit) &&
      Number.isInteger(probationValue) &&
      probationValue > 0

    if (!hasProbation) {
      return {
        enabled: false,
        startDateLabel: "",
        endDateLabel: "",
        message: "No probation period is configured.",
      }
    }

    const todayDateLabel = new Date().toISOString().slice(0, 10)
    let probationStartDate = null
    if (form.enrollmentEnabled) {
      if (form.enrollmentEndDate) {
        probationStartDate = new Date(`${form.enrollmentEndDate}T00:00:00`)
      }
    } else {
      probationStartDate = new Date(`${todayDateLabel}T00:00:00`)
    }

    if (!probationStartDate || Number.isNaN(probationStartDate.getTime())) {
      return {
        enabled: true,
        startDateLabel: "",
        endDateLabel: "",
        message: "Set enrollment end date to preview probation start and end dates.",
      }
    }

    const probationEndDate = new Date(probationStartDate)
    if (probationUnit === "days") {
      probationEndDate.setDate(probationEndDate.getDate() + probationValue)
    } else {
      probationEndDate.setMonth(probationEndDate.getMonth() + probationValue)
    }

    const toLabel = (value) => {
      const dt = new Date(value)
      if (Number.isNaN(dt.getTime())) return ""
      return dt.toISOString().slice(0, 10)
    }

    return {
      enabled: true,
      startDateLabel: toLabel(probationStartDate),
      endDateLabel: toLabel(probationEndDate),
      message: "",
    }
  }, [
    form.probationUnit,
    form.probationValue,
    form.enrollmentEnabled,
    form.enrollmentEndDate,
  ])

  const maxChildDateInput = new Date().toISOString().slice(0, 10)
  const minChildDateInput = (() => {
    const dt = new Date()
    dt.setMonth(dt.getMonth() - 6)
    return dt.toISOString().slice(0, 10)
  })()
  const welfareRelationSummaryRows = useMemo(
    () => [
      {
        label: "Member",
        covered: true,
        benefit: Number(benefitAmounts?.member || 0),
        reimbursement: Number(reimbursementAmounts?.member || 0),
      },
      {
        label: "Spouse",
        covered: coverage?.spouse !== false,
        benefit: Number(benefitAmounts?.spouse || 0),
        reimbursement: Number(reimbursementAmounts?.spouse || 0),
      },
      {
        label: "Parent",
        covered: coverage?.parent !== false,
        benefit: Number(benefitAmounts?.parent || 0),
        reimbursement: Number(reimbursementAmounts?.parent || 0),
      },
      {
        label: "Spouse Parent",
        covered: coverage?.spouse !== false && coverage?.spouseParent !== false,
        benefit: Number(benefitAmounts?.spouse_parent ?? benefitAmounts?.spouseParent ?? 0),
        reimbursement: Number(
          reimbursementAmounts?.spouse_parent ?? reimbursementAmounts?.spouseParent ?? 0
        ),
      },
      {
        label: "Child",
        covered: coverage?.child !== false,
        benefit: Number(benefitAmounts?.child || 0),
        reimbursement: Number(reimbursementAmounts?.child || 0),
      },
      {
        label: "Sibling",
        covered: coverage?.sibling !== false,
        benefit: Number(benefitAmounts?.sibling || 0),
        reimbursement: Number(reimbursementAmounts?.sibling || 0),
      },
    ],
    [
      benefitAmounts?.member,
      benefitAmounts?.spouse,
      benefitAmounts?.parent,
      benefitAmounts?.spouse_parent,
      benefitAmounts?.spouseParent,
      benefitAmounts?.child,
      benefitAmounts?.sibling,
      reimbursementAmounts?.member,
      reimbursementAmounts?.spouse,
      reimbursementAmounts?.parent,
      reimbursementAmounts?.spouse_parent,
      reimbursementAmounts?.spouseParent,
      reimbursementAmounts?.child,
      reimbursementAmounts?.sibling,
      coverage?.spouse,
      coverage?.parent,
      coverage?.spouseParent,
      coverage?.child,
      coverage?.sibling,
    ]
  )

  const validate = () => {
    if (form.enrollmentEnabled && !form.enrollmentStartDate) {
      return "Enrollment start date is required while enrollment is enabled."
    }

    if (form.enrollmentStartDate && form.enrollmentEndDate) {
      const start = new Date(`${form.enrollmentStartDate}T00:00:00`)
      const end = new Date(`${form.enrollmentEndDate}T00:00:00`)
      if (!Number.isNaN(start.getTime()) && !Number.isNaN(end.getTime()) && end.getTime() < start.getTime()) {
        return "Enrollment end date cannot be before start date."
      }
    }

    const registrationFee = Number(form.registrationFee)
    if (!Number.isFinite(registrationFee) || registrationFee <= 0) {
      return "Registration fee must be a number greater than 0."
    }

    const lateFee = Number(form.lateFee)
    if (!Number.isFinite(lateFee) || lateFee < 0) {
      return "Late fee must be a number greater than or equal to 0."
    }

    const benefitValues = [
      form.benefitMember,
      form.benefitSpouse,
      form.benefitSpouseParent,
      form.benefitChild,
      form.benefitSibling,
      form.benefitParent,
    ]
    for (const raw of benefitValues) {
      const value = Number(raw)
      if (!Number.isFinite(value) || value < 0) {
        return "All benefit amounts must be numbers greater than or equal to 0."
      }
    }

    const reimbursementValues = [
      form.reimbursementMember,
      form.reimbursementSpouse,
      form.reimbursementSpouseParent,
      form.reimbursementChild,
      form.reimbursementSibling,
      form.reimbursementParent,
    ]
    for (const raw of reimbursementValues) {
      const value = Number(raw)
      if (!Number.isFinite(value) || value < 0) {
        return "All reimbursement amounts must be numbers greater than or equal to 0."
      }
    }

    const probationUnit = String(form.probationUnit || "").trim().toLowerCase()
    const probationValueRaw = String(form.probationValue || "").trim()
    const probationValue = Number(probationValueRaw)
    const probationUnitProvided = ["days", "months"].includes(probationUnit)
    const probationValueProvided = probationValueRaw !== ""
    const probationValueIsZero = probationValueProvided && Number.isFinite(probationValue) && probationValue === 0
    const probationValueIsPositiveInteger =
      probationValueProvided && Number.isInteger(probationValue) && probationValue > 0

    // "0" is treated as "No probation".
    if (probationValueIsZero) return ""

    if (!probationUnitProvided && !probationValueProvided) return ""

    if (probationUnitProvided && !probationValueIsPositiveInteger) {
      return "Probation value must be a whole number greater than 0."
    }

    if (!probationUnitProvided && probationValueProvided) {
      return "Select probation unit or set No probation."
    }

    if (probationValueProvided && !Number.isInteger(probationValue)) {
      return "Probation value must be a whole number greater than 0."
    }

    return ""
  }

  const handleSave = async () => {
    if (readOnly) return
    setError("")
    setSuccess("")

    if (!isAdmin) {
      setError("Only admins can update Welfare settings.")
      return
    }

    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    const probationValue = Number(form.probationValue)
    const probationUnit = String(form.probationUnit || "").toLowerCase()
    const probationEnabled =
      Number.isInteger(probationValue) &&
      probationValue > 0 &&
      ["days", "months"].includes(probationUnit)
    const payload = {
      enrollmentEnabled: !!form.enrollmentEnabled,
      enrollmentStartDate: form.enrollmentStartDate || null,
      enrollmentEndDate: form.enrollmentEndDate || null,
      registrationFee: Number(form.registrationFee || 0),
      lateFee: Number(form.lateFee || 0),
      benefitAmounts: {
        member: Number(form.benefitMember || 0),
        spouse: Number(form.benefitSpouse || 0),
        spouse_parent: Number(form.benefitSpouseParent || 0),
        child: Number(form.benefitChild || 0),
        sibling: Number(form.benefitSibling || 0),
        parent: Number(form.benefitParent || 0),
      },
      reimbursementAmounts: {
        member: Number(form.reimbursementMember || 0),
        spouse: Number(form.reimbursementSpouse || 0),
        spouse_parent: Number(form.reimbursementSpouseParent || 0),
        child: Number(form.reimbursementChild || 0),
        sibling: Number(form.reimbursementSibling || 0),
        parent: Number(form.reimbursementParent || 0),
      },
      coverage: {
        parent: !!form.coverParent,
        spouse: !!form.coverSpouse,
        spouseParent: !!(form.coverSpouse && form.coverSpouseParent),
        child: !!form.coverChild,
        sibling: !!form.coverSibling,
      },
      probationValue: probationEnabled ? probationValue : null,
      probationUnit: probationEnabled ? probationUnit : "",
    }

    const result = await onSaveSettings?.(payload)
    if (!result?.ok) {
      setError(result?.message || "Failed to update Welfare settings.")
      return
    }
    setSuccess(result?.message || "Welfare settings updated.")
  }

  const handleAddChild = async () => {
    if (readOnly) return
    setChildError("")
    setChildSuccess("")
    const fullName = String(childForm.fullName || "").trim()
    if (!fullName) {
      setChildError("Child full name is required.")
      return
    }
    const dateOfBirth = String(childForm.dateOfBirth || "").trim()
    if (!dateOfBirth) {
      setChildError("Child date of birth is required.")
      return
    }

    const now = new Date()
    const dob = new Date(`${dateOfBirth}T00:00:00`)
    if (Number.isNaN(dob.getTime())) {
      setChildError("Child date of birth is invalid.")
      return
    }
    const oldestAllowed = new Date(now)
    oldestAllowed.setMonth(oldestAllowed.getMonth() - 6)
    oldestAllowed.setHours(0, 0, 0, 0)
    if (dob.getTime() < oldestAllowed.getTime()) {
      setChildError("Child date of birth must be within the last 6 months.")
      return
    }
    if (dob.getTime() > now.getTime()) {
      setChildError("Child date of birth cannot be in the future.")
      return
    }

    const result = await onAddChild?.({
      fullName,
      dateOfBirth,
    })
    if (!result?.ok) {
      setChildError(result?.message || "Failed to add child.")
      return
    }
    setChildForm({ fullName: "", dateOfBirth: "" })
    setChildSuccess(result?.message || "Child added successfully.")
  }

  return (
    <div className="space-y-2">
      {readOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Archived view is read-only. Reactivate the group to change welfare settings.
        </div>
      )}
      <fieldset
        disabled={readOnly}
        className={`min-w-0 border-0 p-0 m-0 ${readOnly ? "opacity-75" : ""}`}
      >
    <div className={`bg-white/95 rounded-2xl border ${theme.border} shadow-md p-4 space-y-4`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-gray-900">Welfare Settings</h3>
          <p className="text-xs text-gray-500 mt-1">
            Manage enrollment window, fees, and payout rules.
          </p>
        </div>
        <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
          welfare?.isEnrollmentOpen ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-gray-100 text-gray-700 border-gray-200"
        }`}>
          {welfare?.isEnrollmentOpen ? "Enrollment Open" : "Enrollment Closed"}
        </span>
      </div>

      {isAdmin ? (
        <>
          <div className={`rounded-xl border ${theme.border} ${theme.mutedBg} p-3 space-y-3`}>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-gray-700">Enrollment enabled</label>
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, enrollmentEnabled: !prev.enrollmentEnabled }))}
                className={`relative inline-flex h-6 w-14 items-center rounded-full border transition-all shadow-sm ${
                  form.enrollmentEnabled
                    ? "bg-[#3b82f6] border-[#3b82f6]"
                    : "bg-gray-200 border-gray-300"
                }`}
              >
                <span
                  className={`absolute text-[9px] font-black tracking-tight ${
                    form.enrollmentEnabled ? "left-2 text-white" : "right-2 text-gray-500"
                  }`}
                >
                  {form.enrollmentEnabled ? "ON" : "OFF"}
                </span>
                <span
                  className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                    form.enrollmentEnabled ? "translate-x-8" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Start date</label>
                <input
                  type="date"
                  value={form.enrollmentStartDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, enrollmentStartDate: e.target.value }))}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">End date (optional)</label>
                <input
                  type="date"
                  value={form.enrollmentEndDate}
                  onChange={(e) => setForm((prev) => ({ ...prev, enrollmentEndDate: e.target.value }))}
                  min={form.enrollmentStartDate || ""}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Registration Fee (KSh)</label>
                <input
                  type="number"
                  min="0.01"
                  step="0.01"
                  value={form.registrationFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, registrationFee: e.target.value }))}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                />
              </div>
              <div>
                <label className="block text-[11px] font-medium text-gray-700 mb-1">Late Fee (KSh)</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={form.lateFee}
                  onChange={(e) => setForm((prev) => ({ ...prev, lateFee: e.target.value }))}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                />
              </div>
            </div>

            <div className="pt-2 border-t border-gray-200/80 space-y-2">
              <div className="flex items-center gap-2 text-gray-700">
                <Shield size={14} />
                <p className="text-xs font-semibold">Probation</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Duration</label>
                  <select
                    value={form.probationUnit}
                    onChange={(e) => {
                      const nextUnit = e.target.value
                      setForm((prev) => ({
                        ...prev,
                        probationUnit: nextUnit,
                        probationValue: nextUnit ? prev.probationValue : "",
                      }))
                    }}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                  >
                    <option value="">No probation</option>
                    <option value="days">Days</option>
                    <option value="months">Months</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-medium text-gray-700 mb-1">Length</label>
                  <input
                    type="number"
                    min="1"
                    step="1"
                    value={form.probationValue}
                    onChange={(e) => {
                      const nextValue = e.target.value
                      const parsed = Number(nextValue)
                      // "0" is treated as "No probation".
                      if (nextValue !== "" && Number.isFinite(parsed) && parsed === 0) {
                        setForm((prev) => ({ ...prev, probationValue: "", probationUnit: "" }))
                        return
                      }
                      setForm((prev) => ({ ...prev, probationValue: nextValue }))
                    }}
                    disabled={!["days", "months"].includes(String(form.probationUnit || "").toLowerCase())}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                    placeholder="e.g. 30"
                  />
                </div>
              </div>
              {welfareProbationPreview.enabled &&
              welfareProbationPreview.startDateLabel &&
              welfareProbationPreview.endDateLabel ? (
                <div className="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-[11px] text-blue-800">
                  <p>
                    Start date: <span className="font-semibold">{welfareProbationPreview.startDateLabel}</span>
                  </p>
                  <p>
                    End date: <span className="font-semibold">{welfareProbationPreview.endDateLabel}</span>
                  </p>
                </div>
              ) : (
                <p className="text-[11px] text-gray-500">{welfareProbationPreview.message}</p>
              )}
            </div>

            <div className="flex justify-end pt-1">
              <button
                type="button"
                onClick={handleSave}
                disabled={isSaving}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 ${theme.button}`}
              >
                {isSaving ? "Saving..." : "Update"}
              </button>
            </div>
          </div>

          <div className={`rounded-xl border ${theme.border} ${theme.mutedBg} p-3 space-y-2`}>
            <p className="text-xs font-semibold text-gray-700">Covered Relations (Onboarding + Loss Reports)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {[
                { key: "coverSpouse", label: "Spouse" },
                { key: "coverParent", label: "Parents" },
                {
                  key: "coverSpouseParent",
                  label: "Spouse Parents",
                  disabled: !form.coverSpouse,
                },
                { key: "coverChild", label: "Children" },
                { key: "coverSibling", label: "Siblings" },
              ].map((row) => (
                <div
                  key={row.key}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-3 py-2"
                >
                  <span className="text-xs text-gray-700">{row.label}</span>
                  <button
                    type="button"
                    disabled={!!row.disabled}
                    onClick={() =>
                      setForm((prev) => {
                        const next = {
                          ...prev,
                          [row.key]: !prev[row.key],
                        }
                        if (row.key === "coverSpouse" && !next.coverSpouse) {
                          next.coverSpouseParent = false
                        }
                        return next
                      })
                    }
                    className={`relative inline-flex h-6 w-14 items-center rounded-full border transition-all shadow-sm disabled:opacity-60 ${
                      form[row.key]
                        ? "bg-[#3b82f6] border-[#3b82f6]"
                        : "bg-gray-200 border-gray-300"
                    }`}
                  >
                    <span
                      className={`absolute text-[9px] font-black tracking-tight ${
                        form[row.key] ? "left-2 text-white" : "right-2 text-gray-500"
                      }`}
                    >
                      {form[row.key] ? "ON" : "OFF"}
                    </span>
                    <span
                      className={`absolute top-0.5 left-0.5 h-5 w-5 rounded-full bg-white shadow-md ring-1 ring-black/5 transition-transform duration-300 ease-in-out ${
                        form[row.key] ? "translate-x-8" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className={`rounded-xl border ${theme.border} ${theme.mutedBg} p-3 space-y-2`}>
            <div className="flex items-center gap-2 text-gray-700">
              <Shield size={14} />
              <p className="text-xs font-semibold">Eligible amount and Reimbursement Amounts</p>
            </div>

            <div className="space-y-2">
              <div className="hidden sm:grid grid-cols-12 gap-2 px-3">
                <div className="col-span-12 sm:col-span-4" />
                <div className="col-span-6 sm:col-span-4 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                  Eligible amount
                </div>
                <div className="col-span-6 sm:col-span-4 text-[10px] font-semibold uppercase tracking-wide text-gray-600">
                  Reimbursement
                </div>
              </div>
              {[
                {
                  label: "Member",
                  enabled: true,
                  lossReportAmountValue: form.benefitMember,
                  onLossReportAmountChange: (value) => setForm((prev) => ({ ...prev, benefitMember: value })),
                  reimbursementValue: form.reimbursementMember,
                  onReimbursementChange: (value) => setForm((prev) => ({ ...prev, reimbursementMember: value })),
                },
                {
                  label: "Spouse",
                  enabled: !!form.coverSpouse,
                  lossReportAmountValue: form.benefitSpouse,
                  onLossReportAmountChange: (value) => setForm((prev) => ({ ...prev, benefitSpouse: value })),
                  reimbursementValue: form.reimbursementSpouse,
                  onReimbursementChange: (value) => setForm((prev) => ({ ...prev, reimbursementSpouse: value })),
                },
                {
                  label: "Parent",
                  enabled: !!form.coverParent,
                  lossReportAmountValue: form.benefitParent,
                  onLossReportAmountChange: (value) => setForm((prev) => ({ ...prev, benefitParent: value })),
                  reimbursementValue: form.reimbursementParent,
                  onReimbursementChange: (value) => setForm((prev) => ({ ...prev, reimbursementParent: value })),
                },
                {
                  label: "Spouse Parent",
                  enabled: !!form.coverSpouse && !!form.coverSpouseParent,
                  lossReportAmountValue: form.benefitSpouseParent,
                  onLossReportAmountChange: (value) => setForm((prev) => ({ ...prev, benefitSpouseParent: value })),
                  reimbursementValue: form.reimbursementSpouseParent,
                  onReimbursementChange: (value) => setForm((prev) => ({ ...prev, reimbursementSpouseParent: value })),
                },
                {
                  label: "Child",
                  enabled: !!form.coverChild,
                  lossReportAmountValue: form.benefitChild,
                  onLossReportAmountChange: (value) => setForm((prev) => ({ ...prev, benefitChild: value })),
                  reimbursementValue: form.reimbursementChild,
                  onReimbursementChange: (value) => setForm((prev) => ({ ...prev, reimbursementChild: value })),
                },
                {
                  label: "Sibling",
                  enabled: !!form.coverSibling,
                  lossReportAmountValue: form.benefitSibling,
                  onLossReportAmountChange: (value) => setForm((prev) => ({ ...prev, benefitSibling: value })),
                  reimbursementValue: form.reimbursementSibling,
                  onReimbursementChange: (value) => setForm((prev) => ({ ...prev, reimbursementSibling: value })),
                },
              ].map((row) => (
                <div
                  key={row.label}
                  className={`grid grid-cols-12 gap-2 items-center rounded-lg border border-gray-200 bg-white px-3 py-2 ${
                    row.enabled ? "" : "opacity-60"
                  }`}
                >
                  <div className="col-span-12 sm:col-span-4">
                    <div className="flex items-center justify-between">
                      <label className="block text-[11px] font-semibold text-gray-700">
                        {row.label}
                      </label>
                      <Pencil className="w-3 h-3 text-gray-400" />
                    </div>
                  </div>
                  <div className="col-span-6 sm:col-span-4">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-600 sm:hidden">
                      Eligible amount
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.lossReportAmountValue || ""}
                      onChange={(e) => row.onLossReportAmountChange?.(e.target.value)}
                      disabled={!row.enabled}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                      placeholder="Eligible amount"
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-4">
                    <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-gray-600 sm:hidden">
                      Reimbursement
                    </label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={row.reimbursementValue || ""}
                      onChange={(e) => row.onReimbursementChange?.(e.target.value)}
                      disabled={!row.enabled}
                      className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
                      placeholder="Reimbursement"
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              onClick={handleSave}
              disabled={isSaving}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 ${theme.button}`}
            >
              {isSaving ? "Saving..." : "Save Welfare Settings"}
            </button>
          </div>
        </>
      ) : (
        <div className="rounded-xl border border-gray-200 bg-white p-3 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Enrollment
              </p>
              <p className="mt-0.5 text-xs font-semibold text-gray-900">
                {enrollment?.enabled ? "Enabled" : "Disabled"}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2 sm:col-span-2 xl:col-span-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Enrollment Window
              </p>
              <p className="mt-0.5 text-xs font-semibold text-gray-900">
                {enrollmentWindowText}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Late Fee
              </p>
              <p className="mt-0.5 text-xs font-semibold text-gray-900">
                {formatMoney(Number(welfare?.lateFee || 0))}
              </p>
            </div>
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-2">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                Probation
              </p>
              <p className="mt-0.5 text-xs font-semibold text-gray-900">
                {welfare?.probation?.value && welfare?.probation?.unit
                  ? `${welfare.probation.value} ${welfare.probation.unit}`
                  : "None"}
              </p>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 overflow-hidden">
            <table className="w-full table-fixed text-[9px] sm:text-[10px]">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="w-[32%] px-1.5 py-1.5 text-left font-semibold uppercase text-[8px] sm:text-[9px]">
                    Relation
                  </th>
                  <th className="w-[18%] px-1.5 py-1.5 text-left font-semibold uppercase text-[8px] sm:text-[9px]">
                    Covered
                  </th>
                  <th className="w-[25%] px-1.5 py-1.5 text-right font-semibold uppercase text-[8px] sm:text-[9px]">
                    Eligible
                  </th>
                  <th className="w-[25%] px-1.5 py-1.5 text-right font-semibold uppercase text-[8px] sm:text-[9px]">
                    Reimb.
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-gray-700">
                {welfareRelationSummaryRows.map((row) => (
                  <tr key={row.label} className="hover:bg-gray-50/70">
                    <td className="px-1.5 py-1.5 font-medium text-gray-900 leading-tight">{row.label}</td>
                    <td className="px-1.5 py-1.5">
                      <span
                        className={`inline-flex items-center rounded-md border px-1 py-0.5 text-[8px] sm:text-[9px] font-semibold ${
                          row.covered
                            ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                            : "border-rose-200 bg-rose-50 text-rose-700"
                        }`}
                      >
                        {row.covered ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-1.5 py-1.5 text-right font-semibold leading-tight">
                      {formatMoney(row.benefit)}
                    </td>
                    <td className="px-1.5 py-1.5 text-right font-semibold leading-tight">
                      {formatMoney(row.reimbursement)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className={`rounded-xl border ${theme.border} ${theme.mutedBg} p-3 space-y-3`}>
        <div className="flex items-center gap-2 text-gray-700">
          <Baby size={14} />
          <p className="text-xs font-semibold">Add Child (Post-Join)</p>
        </div>
        <p className="text-[11px] text-gray-600">
          Add a newborn child to your Welfare family list. Date of birth must be within the last 6 months.
        </p>
        {coverage?.child === false && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
            Child coverage is disabled in Welfare settings, so added children are not allowed.
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Child full name</label>
            <input
              type="text"
              value={childForm.fullName}
              onChange={(e) => setChildForm((prev) => ({ ...prev, fullName: e.target.value }))}
              placeholder="Enter full name"
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
            />
          </div>
          <div>
            <label className="block text-[11px] font-medium text-gray-700 mb-1">Date of birth</label>
            <input
              type="date"
              value={childForm.dateOfBirth}
              onChange={(e) => setChildForm((prev) => ({ ...prev, dateOfBirth: e.target.value }))}
              min={minChildDateInput}
              max={maxChildDateInput}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white focus:outline-none focus:ring-2 ${theme.ring}`}
            />
          </div>
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleAddChild}
            disabled={isAddingChild || coverage?.child === false || !onAddChild}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold text-white disabled:opacity-60 ${theme.button}`}
          >
            {isAddingChild ? "Adding..." : "Add Child"}
          </button>
        </div>
        {!!childError && (
          <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
            {childError}
          </div>
        )}
        {!!childSuccess && (
          <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-700">
            {childSuccess}
          </div>
        )}
      </div>

      {!!error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
      {!!success && (
        <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-700">
          {success}
        </div>
      )}
    </div>
      </fieldset>
    </div>
  )
}

export default GroupWelfareSettingsPanel
